import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  Users as UsersIcon,
  Edit2,
  UserCheck,
  UserX,
  Shield,
  Mail,
  Phone,
  Plus,
  UserPlus,
  Copy,
  Check,
  Clock,
  AlertTriangle,
  Trash2,
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  phone?: string;
  active: number;
  created_at: string;
}

interface Invitation {
  id: string;
  email: string;
  token: string;
  role: string;
  invited_by_name?: string;
  expires_at: string;
  used_at?: string;
  created_at: string;
}

export function Users() {
  const queryClient = useQueryClient();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get<User[]>('/users');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const { data: invitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const res = await api.get<Invitation[]>('/invitations');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const deleteInvitationMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/invitations/${id}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="error"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
      case 'manager':
        return <Badge variant="warning">Manager</Badge>;
      case 'user':
        return <Badge variant="primary">User</Badge>;
      case 'viewer':
        return <Badge variant="neutral">Viewer</Badge>;
      default:
        return <Badge variant="neutral">{role}</Badge>;
    }
  };

  const getInvitationStatus = (invitation: Invitation) => {
    if (invitation.used_at) {
      return <Badge variant="success"><Check className="h-3 w-3 mr-1" />Registered</Badge>;
    }
    const isExpired = new Date(invitation.expires_at) < new Date();
    if (isExpired) {
      return <Badge variant="error"><AlertTriangle className="h-3 w-3 mr-1" />Expired</Badge>;
    }
    return <Badge variant="warning"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  const copyToClipboard = async (token: string) => {
    const url = `${window.location.origin}/register/${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const pendingInvitations = invitations?.filter(inv => !inv.used_at) || [];
  const usedInvitations = invitations?.filter(inv => inv.used_at) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-foreground-muted">Manage users and invitations</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <Plus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium transition-colors relative ${
            activeTab === 'users'
              ? 'text-foreground'
              : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          Users ({users?.length || 0})
          {activeTab === 'users' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-4 py-2 font-medium transition-colors relative ${
            activeTab === 'invitations'
              ? 'text-foreground'
              : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          Invitations ({pendingInvitations.length})
          {activeTab === 'invitations' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card>
          <CardBody>
            {usersLoading ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : users?.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <UsersIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No users</p>
                <p className="text-sm">Invite users to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users?.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium">{user.name}</td>
                        <td>
                          <span className="flex items-center gap-2 text-foreground-muted text-sm">
                            <Mail className="h-4 w-4" />
                            {user.email}
                          </span>
                        </td>
                        <td>
                          {user.phone ? (
                            <span className="flex items-center gap-2 text-foreground-muted text-sm">
                              <Phone className="h-4 w-4" />
                              {user.phone}
                            </span>
                          ) : (
                            <span className="text-foreground-subtle">-</span>
                          )}
                        </td>
                        <td>{getRoleBadge(user.role)}</td>
                        <td>
                          {user.active ? (
                            <Badge variant="success">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="neutral">
                              <UserX className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="text-foreground-muted text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingUser(user)}
                            title="Edit user"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="space-y-6">
          {/* Pending Invitations */}
          <Card>
            <CardBody>
              <h3 className="font-semibold mb-4">Pending Invitations</h3>
              {invitationsLoading ? (
                <div className="text-center py-12">
                  <div className="spinner h-8 w-8 mx-auto" />
                </div>
              ) : pendingInvitations.length === 0 ? (
                <div className="text-center py-8 text-foreground-muted">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No pending invitations</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Invited By</th>
                        <th>Expires</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingInvitations.map((invitation) => (
                        <tr key={invitation.id}>
                          <td>
                            <span className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-foreground-muted" />
                              {invitation.email}
                            </span>
                          </td>
                          <td>{getRoleBadge(invitation.role)}</td>
                          <td className="text-foreground-muted text-sm">
                            {invitation.invited_by_name || '-'}
                          </td>
                          <td className="text-foreground-muted text-sm">
                            {new Date(invitation.expires_at).toLocaleDateString()}
                          </td>
                          <td>{getInvitationStatus(invitation)}</td>
                          <td>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(invitation.token)}
                                title="Copy invite link"
                              >
                                {copiedToken === invitation.token ? (
                                  <Check className="h-4 w-4 text-success" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (confirm('Delete this invitation?')) {
                                    deleteInvitationMutation.mutate(invitation.id);
                                  }
                                }}
                                title="Delete invitation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Used Invitations (collapsed by default) */}
          {usedInvitations.length > 0 && (
            <Card>
              <CardBody>
                <h3 className="font-semibold mb-4 text-foreground-muted">Registered ({usedInvitations.length})</h3>
                <div className="overflow-x-auto">
                  <table className="table opacity-60">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usedInvitations.map((invitation) => (
                        <tr key={invitation.id}>
                          <td className="text-sm">{invitation.email}</td>
                          <td>{getRoleBadge(invitation.role)}</td>
                          <td className="text-foreground-muted text-sm">
                            {invitation.used_at ? new Date(invitation.used_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          queryClient.invalidateQueries({ queryKey: ['invitations'] });
          setActiveTab('invitations');
        }}
      />
    </div>
  );
}

// Edit User Modal
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

function EditUserModal({ user, onClose, onSuccess }: EditUserModalProps) {
  const [formData, setFormData] = useState({
    name: user.name,
    role: user.role,
    phone: user.phone || '',
    active: user.active === 1,
  });
  const [error, setError] = useState('');

  const roleOptions = [
    { value: 'admin', label: 'Admin - Full access' },
    { value: 'manager', label: 'Manager - Manage inventory & jobs' },
    { value: 'user', label: 'User - Checkout and return tools' },
    { value: 'viewer', label: 'Viewer - Read only access' },
  ];

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.put(`/users/${user.id}`, {
        name: data.name,
        role: data.role,
        phone: data.phone || undefined,
        active: data.active,
      });
      if (!res.success) throw new Error(res.error || 'Failed to update user');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    },
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit User: ${user.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(formData)} loading={mutation.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <p className="text-foreground-muted bg-surface-hover rounded-lg px-3 py-2 text-sm">{user.email}</p>
          <p className="text-xs text-foreground-subtle mt-1">Email cannot be changed</p>
        </div>

        <Input
          label="Phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="Optional"
        />

        <Select
          label="Role"
          options={roleOptions}
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
        />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="rounded border-border"
          />
          <span className="text-sm">Account active</span>
        </label>
      </div>
    </Modal>
  );
}

// Invite Modal
interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function InviteModal({ isOpen, onClose, onSuccess }: InviteModalProps) {
  const [formData, setFormData] = useState({ email: '', role: 'user', expiresInDays: '7' });
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const roleOptions = [
    { value: 'admin', label: 'Admin - Full access' },
    { value: 'manager', label: 'Manager - Manage inventory & jobs' },
    { value: 'user', label: 'User - Checkout and return tools' },
    { value: 'viewer', label: 'Viewer - Read only access' },
  ];

  const expiryOptions = [
    { value: '1', label: '1 day' },
    { value: '7', label: '7 days' },
    { value: '14', label: '14 days' },
    { value: '30', label: '30 days' },
  ];

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post<{ token: string; registrationUrl: string }>('/invitations', {
        email: data.email,
        role: data.role,
        expiresInDays: parseInt(data.expiresInDays),
      });
      if (!res.success) throw new Error(res.error || 'Failed to create invitation');
      return res.data;
    },
    onSuccess: (data) => {
      setInviteUrl(`${window.location.origin}${data?.registrationUrl}`);
      setError('');
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create invitation');
    },
  });

  const handleClose = () => {
    setFormData({ email: '', role: 'user', expiresInDays: '7' });
    setError('');
    if (inviteUrl) onSuccess();
    setInviteUrl(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite User"
      footer={
        inviteUrl ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={handleClose}>Cancel</Button>
            <Button onClick={() => mutation.mutate(formData)} loading={mutation.isPending}>
              <UserPlus className="h-4 w-4" />
              Create Invitation
            </Button>
          </>
        )
      }
    >
      {inviteUrl ? (
        <div className="space-y-4">
          <div className="p-4 bg-success/10 border border-success/30 rounded-lg text-center">
            <Check className="h-8 w-8 mx-auto mb-2 text-success" />
            <p className="font-medium text-lg">Invitation Created!</p>
            <p className="text-sm text-foreground-muted mt-1">Copy and send this link to {formData.email}</p>
          </div>

          <div className="p-4 bg-surface-hover border border-border rounded-lg space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground-muted mb-1">Instructions for the user:</p>
              <ol className="text-sm space-y-1 list-decimal list-inside text-foreground-muted">
                <li>Click the registration link</li>
                <li>Enter name and create a password</li>
                <li>Click Register to complete setup</li>
              </ol>
            </div>

            <div className="pt-2 border-t border-border">
              <p className="text-xs text-foreground-subtle">
                Role: <span className="font-medium text-foreground">{formData.role}</span> •
                Expires: <span className="font-medium text-foreground">{formData.expiresInDays} days</span>
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Registration Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteUrl}
                readOnly
                onClick={(e) => {
                  (e.target as HTMLInputElement).select();
                  navigator.clipboard.writeText(inviteUrl);
                }}
                className="flex-1 bg-input border border-border rounded-lg px-3 py-2 text-sm font-mono cursor-pointer hover:bg-input-focus transition-colors"
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(inviteUrl);
                  alert('Link copied!');
                }}
                title="Copy link"
              >
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-foreground-subtle mt-2">
              💡 Send via email, text, or messaging app
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

          <Input
            label="Email Address *"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
          />

          <Select
            label="Role"
            options={roleOptions}
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          />

          <Select
            label="Expires In"
            options={expiryOptions}
            value={formData.expiresInDays}
            onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
          />
        </div>
      )}
    </Modal>
  );
}
